/**
 * Super-admin Accounting page — single component with 8 tabs:
 * COA / Journal / Ledger / Reports / Invoices / GST / Periods / Health.
 *
 * Keeps things lean: one file, plain tables, no chart libraries.
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import { api, apiError } from "@/lib/api";
import { authDownload } from "@/lib/authDownload";
import { PageHeader } from "@/components/Ui";
import { SearchableSelect } from "@/components/SearchableSelect";
import InvoiceShareModal from "@/components/InvoiceShareModal";
import { fmtDateTime, fmtDate } from "@/lib/time";
import { Layers, BookOpen, FileText, TrendingUp, Receipt, Percent, Lock, Activity, RefreshCw, Plus, Download, Upload, Sparkles, FileCheck2, Wand2, Share2, Search, ChevronRight, ChevronDown, X, Trash2, Undo2, Mail, Send, History } from "lucide-react";
import { useTableControls, SortableTh, FilterChip } from "@/components/SortableTable";

const TABS = [
    { id: "coa",       label: "Chart of Accounts", Icon: Layers },
    { id: "journal",   label: "Journal",           Icon: BookOpen },
    { id: "ledger",    label: "Ledger",            Icon: FileText },
    { id: "reports",   label: "Reports",           Icon: TrendingUp },
    { id: "invoices",  label: "Invoices",          Icon: Receipt },
    { id: "recon",     label: "Bank Recon",        Icon: FileCheck2 },
    { id: "gstr",      label: "GST Returns",       Icon: Wand2 },
    { id: "gst",       label: "GST Config",        Icon: Percent },
    { id: "periods",   label: "Periods",           Icon: Lock },
    { id: "health",    label: "Health",            Icon: Activity },
];

const inr = (n) => n == null ? "—" : `₹${Number(n).toLocaleString("en-IN", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

export default function AdminAccounting() {
    const [tab, setTab] = useState("reports");
    return (
        <>
            <PageHeader title="Accounting & Ledger"
                subtitle="Double-entry books · GST-compliant invoicing · live P&L and balance sheet · period-locked. Auto-posts every Razorpay subscription, wallet top-up, AI charge and referral pay-out into the ledger."/>
            <div className="flex gap-1 border-b border-border mb-6 overflow-x-auto">
                {TABS.map(t => (
                    <button key={t.id} onClick={() => setTab(t.id)} data-testid={`acc-tab-${t.id}`}
                        className={`px-3 py-2.5 text-[10px] uppercase tracking-[0.16em] border-b-2 -mb-px inline-flex items-center gap-1.5 whitespace-nowrap ${
                            tab === t.id ? "border-primary text-primary" : "border-transparent text-muted-foreground hover:text-foreground"
                        }`}>
                        <t.Icon className="w-3.5 h-3.5" strokeWidth={1.5}/>{t.label}
                    </button>
                ))}
            </div>
            {tab === "coa" && <CoaPanel/>}
            {tab === "journal" && <JournalPanel/>}
            {tab === "ledger" && <LedgerPanel/>}
            {tab === "reports" && <ReportsPanel/>}
            {tab === "invoices" && <InvoicesPanel/>}
            {tab === "recon" && <ReconPanel/>}
            {tab === "gstr" && <GstrPanel/>}
            {tab === "gst" && <GstPanel/>}
            {tab === "periods" && <PeriodsPanel/>}
            {tab === "health" && <HealthPanel/>}
        </>
    );
}

function useFetch(url, dep = []) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(false);
    const reload = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get(url); setData(data); }
        catch (e) { toast.error(apiError(e)); } finally { setLoading(false); }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, dep);
    useEffect(() => { reload(); }, [reload]);  // eslint-disable-line
    return { data, loading, reload, setData };
}

function CoaPanel() {
    const [rows, setRows] = useState([]);
    const [loading, setLoading] = useState(true);
    const [showAdd, setShowAdd] = useState(false);
    const [editing, setEditing] = useState(null);
    const [addParent, setAddParent] = useState(null);
    const [query, setQuery] = useState("");
    const [expanded, setExpanded] = useState(() => new Set());
    const [syncing, setSyncing] = useState(false);

    const load = useCallback(async () => {
        setLoading(true);
        try { const { data } = await api.get("/admin/accounting/coa"); setRows(data || []); }
        catch (e) { toast.error(apiError(e)); }
        finally { setLoading(false); }
    }, []);
    useEffect(() => { load(); }, [load]);

    const { topLevels, childrenOf } = useMemo(() => {
        const top = []; const ch = new Map();
        rows.forEach(r => {
            if (!r.parent_code) top.push(r);
            else { const arr = ch.get(r.parent_code) || []; arr.push(r); ch.set(r.parent_code, arr); }
        });
        const cmp = (a, b) => a.code.localeCompare(b.code, undefined, { numeric: true });
        top.sort(cmp); ch.forEach(arr => arr.sort(cmp));
        return { topLevels: top, childrenOf: ch };
    }, [rows]);

    const q = query.trim().toLowerCase();
    const matchSet = useMemo(() => {
        if (!q) return null;
        const m = new Set(); const byCode = new Map(rows.map(r => [r.code, r]));
        rows.forEach(r => {
            if (r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q)) {
                m.add(r.code);
                let p = r.parent_code;
                while (p) { m.add(p); p = byCode.get(p)?.parent_code; }
            }
        });
        return m;
    }, [q, rows]);

    const toggle = (code) => {
        setExpanded(prev => { const n = new Set(prev); n.has(code) ? n.delete(code) : n.add(code); return n; });
    };
    const isOpen = (code) => q ? matchSet?.has(code) : expanded.has(code);

    const handleSync = async () => {
        if (!window.confirm("Push every active platform-level account to every tenant that's missing it? This is idempotent — existing tenant accounts are not overwritten.")) return;
        setSyncing(true);
        try {
            const { data } = await api.post("/admin/accounting/coa/sync-to-tenants");
            toast.success(`Synced. ${data.accounts_added} accounts added across ${data.tenants} tenants.`);
        } catch (e) { toast.error(apiError(e)); } finally { setSyncing(false); }
    };

    const renderRow = (r, depth) => {
        const kids = childrenOf.get(r.code) || [];
        const open = isOpen(r.code);
        const visible = !q || matchSet?.has(r.code);
        if (!visible) return null;
        const canAddChild = depth < 2;
        const isMatch = matchSet && (r.code.toLowerCase().includes(q) || r.name.toLowerCase().includes(q));
        return (
            <div key={r.id}>
                <div className={`flex items-center gap-2 px-2 sm:px-3 py-2 border-b border-border hover:bg-muted/40 ${isMatch ? "bg-primary/5" : ""}`}
                     data-testid={`acc-coa-row-${r.code}`}>
                    <div style={{ paddingLeft: depth * 14 }} className="flex items-center gap-1.5 flex-1 min-w-0">
                        {kids.length > 0 ? (
                            <button onClick={() => toggle(r.code)}
                                data-testid={`acc-coa-toggle-${r.code}`}
                                className="text-muted-foreground hover:text-foreground p-0.5 shrink-0">
                                {open ? <ChevronDown className="w-3 h-3"/> : <ChevronRight className="w-3 h-3"/>}
                            </button>
                        ) : <div className="w-4 shrink-0"/>}
                        <span className="font-mono text-primary text-xs shrink-0">{r.code}</span>
                        <span className="text-foreground text-xs truncate min-w-0">{r.name}</span>
                        {r.is_system && <span className="hidden sm:inline text-[9px] uppercase tracking-wider text-muted-foreground border border-border px-1 ml-1 shrink-0">sys</span>}
                        {!r.is_active && <span className="text-[9px] uppercase tracking-wider text-rose-400 border border-rose-500/40 px-1 ml-1 shrink-0">inactive</span>}
                    </div>
                    <span className="hidden md:inline text-muted-foreground text-[10px] uppercase tracking-wider w-20 text-right">{r.type}</span>
                    <span className="hidden lg:inline text-muted-foreground text-[10px] uppercase tracking-wider w-14 text-right">{r.normal_balance}</span>
                    <span className="hidden lg:inline text-[10px] w-10 text-center">{r.gst_relevant ? <span className="text-primary">GST</span> : <span className="text-muted-foreground">—</span>}</span>
                    <div className="flex items-center gap-1 justify-end shrink-0">
                        {canAddChild && (
                            <button onClick={() => { setAddParent(r); setShowAdd(true); }}
                                data-testid={`acc-coa-add-child-${r.code}`}
                                title={depth === 0 ? "Add sub-ledger" : "Add sub-account"}
                                aria-label={depth === 0 ? "Add sub-ledger" : "Add sub-account"}
                                className="text-muted-foreground hover:text-primary p-1.5 border border-border hover:border-primary">
                                <Plus className="w-3 h-3"/>
                            </button>
                        )}
                        <button onClick={() => setEditing(r)}
                            data-testid={`acc-coa-edit-${r.code}`}
                            aria-label="Edit"
                            className="text-[9px] uppercase tracking-wider px-2 py-1.5 border border-border text-muted-foreground hover:text-primary hover:border-primary">
                            Edit
                        </button>
                    </div>
                </div>
                {open && kids.map(k => renderRow(k, depth + 1))}
            </div>
        );
    };

    return (
        <div className="space-y-3" data-testid="acc-coa">
            <div className="flex flex-wrap items-center gap-2 justify-between">
                <div className="flex items-center gap-2 flex-1 min-w-[260px]">
                    <div className="relative flex-1 max-w-md">
                        <Search className="w-3 h-3 text-muted-foreground absolute left-3 top-1/2 -translate-y-1/2"/>
                        <input value={query} onChange={e => setQuery(e.target.value)}
                            placeholder="Search code or name (e.g. 5810 or Counsel)"
                            data-testid="acc-coa-search"
                            className="w-full bg-card border border-border pl-8 pr-3 py-2 text-foreground text-xs placeholder:text-muted-foreground"/>
                    </div>
                    <span className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">{rows.length} accounts</span>
                </div>
                <div className="flex items-center gap-2">
                    <button onClick={handleSync} disabled={syncing}
                        data-testid="acc-coa-sync"
                        title="Push every active platform-level account to all tenant books that are missing them"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-border text-muted-foreground hover:text-primary hover:border-primary text-[10px] uppercase tracking-[0.16em] disabled:opacity-50">
                        <RefreshCw className={`w-3 h-3 ${syncing ? "animate-spin" : ""}`}/>{syncing ? "Syncing…" : "Sync to tenants"}
                    </button>
                    <button onClick={() => { setAddParent(null); setShowAdd(true); }}
                        data-testid="acc-coa-add"
                        className="inline-flex items-center gap-1.5 px-3 py-1.5 border border-primary text-primary hover:bg-primary/10 text-[10px] uppercase tracking-[0.16em]">
                        <Plus className="w-3 h-3"/>New account
                    </button>
                </div>
            </div>
            <p className="text-[11px] text-muted-foreground">
                Platform-level Chart of Accounts. Any new account you add here can be auto-pushed to all tenant books.
            </p>
            <div className="border border-border bg-card">
                <div className="flex items-center gap-2 px-2 sm:px-3 py-2 border-b border-border text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    <div className="flex-1">Account</div>
                    <span className="hidden md:inline w-20 text-right">Type</span>
                    <span className="hidden lg:inline w-14 text-right">Normal</span>
                    <span className="hidden lg:inline w-10 text-center">GST</span>
                    <span className="text-right">Actions</span>
                </div>
                {loading && <div className="px-3 py-8 text-center text-muted-foreground text-xs">Loading…</div>}
                {!loading && topLevels.length === 0 && <div className="px-3 py-8 text-center text-muted-foreground text-xs">No accounts found.</div>}
                {!loading && topLevels.map(t => renderRow(t, 0))}
            </div>
            {showAdd && <AdminCoaEditorModal mode="create" initialParent={addParent} allAccounts={rows}
                onClose={() => { setShowAdd(false); setAddParent(null); }}
                onSaved={(msg) => { setShowAdd(false); setAddParent(null); load(); if (msg) toast.success(msg); }}/>}
            {editing && <AdminCoaEditorModal mode="edit" account={editing} allAccounts={rows}
                onClose={() => setEditing(null)}
                onSaved={(msg) => { setEditing(null); load(); if (msg) toast.success(msg); }}/>}
        </div>
    );
}

function AdminCoaEditorModal({ mode, account, initialParent, allAccounts, onClose, onSaved }) {
    const isEdit = mode === "edit";
    const findDepth = (code) => {
        if (!code) return 0;
        const byCode = new Map(allAccounts.map(a => [a.code, a]));
        let d = 0; let cur = byCode.get(code);
        while (cur) { d += 1; cur = cur.parent_code ? byCode.get(cur.parent_code) : null; }
        return d;
    };
    const parentDepth = initialParent ? findDepth(initialParent.code) : 0;
    const defaultKind = isEdit
        ? (account.parent_code ? (findDepth(account.parent_code) === 1 ? "sub_ledger" : "sub_account") : "account")
        : (initialParent ? (parentDepth === 1 ? "sub_ledger" : "sub_account") : "account");

    const [kind, setKind] = useState(defaultKind);
    const [form, setForm] = useState(() => isEdit ? {
        code: account.code, name: account.name, type: account.type,
        parent_code: account.parent_code || "", normal_balance: account.normal_balance,
        gst_relevant: account.gst_relevant, is_active: account.is_active,
    } : {
        code: "", name: "",
        type: initialParent?.type || "expense",
        parent_code: initialParent?.code || "",
        normal_balance: initialParent?.normal_balance || "debit",
        gst_relevant: false, is_active: true,
    });
    const [propagate, setPropagate] = useState(true);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);

    useEffect(() => {
        if (isEdit) return;
        if (kind === "account" && form.code) return;
        const params = kind === "account" ? {} : { parent_code: form.parent_code };
        if (kind !== "account" && !form.parent_code) return;
        api.get("/admin/accounting/coa/next-code", { params }).then(r => {
            if (r.data?.suggested && !form.code) setForm(f => ({ ...f, code: r.data.suggested }));
        }).catch(() => {});
    }, [kind, form.parent_code, isEdit, form.code]);

    useEffect(() => {
        if (isEdit) return;
        if (kind === "account") return;
        const par = allAccounts.find(a => a.code === form.parent_code);
        if (par) setForm(f => ({ ...f, type: par.type, normal_balance: par.normal_balance }));
    }, [form.parent_code, kind, isEdit, allAccounts]);

    const setKindAndReset = (k) => {
        setKind(k);
        if (!isEdit) setForm(f => ({ ...f, parent_code: "", code: "" }));
    };

    const submit = async (e) => {
        e.preventDefault();
        setSaving(true);
        try {
            if (isEdit) {
                await api.patch(`/admin/accounting/coa/${account.id}`, {
                    name: form.name, is_active: form.is_active,
                    gst_relevant: form.gst_relevant,
                });
                onSaved("Account updated");
            } else {
                const payload = {
                    code: form.code.trim(), name: form.name.trim(),
                    type: form.type, normal_balance: form.normal_balance,
                    parent_code: form.parent_code || null,
                    gst_relevant: form.gst_relevant,
                    propagate_to_tenants: propagate,
                };
                const { data } = await api.post("/admin/accounting/coa", payload);
                const msg = propagate && data.propagated_to_tenants
                    ? `Created · pushed to ${data.propagated_to_tenants} tenant${data.propagated_to_tenants === 1 ? "" : "s"}`
                    : (kind === "account" ? "Account created" : kind === "sub_ledger" ? "Sub-ledger created" : "Sub-account created");
                onSaved(msg);
            }
        } catch (err) { toast.error(apiError(err)); }
        finally { setSaving(false); }
    };

    const handleDelete = async () => {
        if (!isEdit || account.is_system) return;
        const cascade = window.confirm(`Delete ${account.code} · ${account.name}?\n\nClick OK to also remove it from every tenant book that has no journal entries on it yet. Click Cancel to delete only from the platform.`);
        setDeleting(true);
        try {
            await api.delete(`/admin/accounting/coa/${account.id}`, { params: { cascade_tenants: cascade } });
            onSaved("Account deleted");
        } catch (err) { toast.error(apiError(err)); }
        finally { setDeleting(false); }
    };

    const title = isEdit
        ? `Edit ${account.code} · ${account.name}`
        : kind === "account" ? "New top-level account"
        : kind === "sub_ledger" ? "New sub-ledger"
        : "New sub-account";

    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <form onSubmit={submit} onClick={e => e.stopPropagation()}
                className="bg-card border border-border w-full max-w-lg p-5 space-y-4 max-h-[90vh] overflow-y-auto"
                data-testid="acc-coa-editor-modal">
                <div className="flex items-center justify-between">
                    <h3 className="text-lg text-foreground">{title}</h3>
                    <button type="button" onClick={onClose} className="text-muted-foreground hover:text-foreground"><X className="w-4 h-4"/></button>
                </div>

                {!isEdit && (
                    <div className="grid grid-cols-3 gap-1 border border-border p-0.5" data-testid="acc-coa-kind-picker">
                        {[{ k: "account", label: "Account" },
                          { k: "sub_ledger", label: "Sub-ledger" },
                          { k: "sub_account", label: "Sub-account" }].map(o => (
                            <button key={o.k} type="button" onClick={() => setKindAndReset(o.k)}
                                data-testid={`acc-coa-kind-${o.k}`}
                                className={`text-[10px] uppercase tracking-[0.16em] py-2 ${kind === o.k ? "bg-primary/10 text-primary" : "text-muted-foreground hover:text-foreground"}`}>
                                {o.label}
                            </button>
                        ))}
                    </div>
                )}

                {!isEdit && kind !== "account" && (
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">
                            {kind === "sub_ledger" ? "Parent account" : "Parent sub-ledger"}
                        </label>
                        <SearchableSelect value={form.parent_code}
                            onChange={(v) => setForm(f => ({ ...f, parent_code: v }))}
                            options={(kind === "sub_ledger"
                                ? allAccounts.filter(a => !a.parent_code)
                                : allAccounts.filter(a => a.parent_code && !allAccounts.find(gp => gp.code === a.parent_code)?.parent_code)
                            ).map(a => ({ value: a.code, label: `${a.code} · ${a.name} (${a.type})` }))}
                            placeholder="Search & select parent…"
                            testId="acc-coa-parent-select"/>
                    </div>
                )}

                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Code</label>
                        <input value={form.code} onChange={e => setForm({...form, code: e.target.value})}
                            placeholder="e.g. 5810" required disabled={isEdit}
                            data-testid="acc-coa-code"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm font-mono disabled:opacity-60"/>
                    </div>
                    <div className="col-span-2">
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Account name</label>
                        <input value={form.name} onChange={e => setForm({...form, name: e.target.value})}
                            placeholder="e.g. Counsel Fees" required
                            data-testid="acc-coa-name"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm"/>
                    </div>
                </div>

                <div className="grid grid-cols-2 gap-3">
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Type</label>
                        <select value={form.type} onChange={e => setForm({...form, type: e.target.value})}
                            disabled={isEdit || (!isEdit && kind !== "account")}
                            data-testid="acc-coa-type"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm disabled:opacity-60">
                            <option value="asset">Asset</option><option value="liability">Liability</option>
                            <option value="equity">Equity</option><option value="income">Income</option>
                            <option value="expense">Expense</option>
                        </select>
                    </div>
                    <div>
                        <label className="block text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Normal balance</label>
                        <select value={form.normal_balance} onChange={e => setForm({...form, normal_balance: e.target.value})}
                            disabled={isEdit || (!isEdit && kind !== "account")}
                            data-testid="acc-coa-normal"
                            className="w-full bg-background border border-border px-3 py-2 text-foreground text-sm disabled:opacity-60">
                            <option value="debit">Debit</option><option value="credit">Credit</option>
                        </select>
                    </div>
                </div>

                <div className="flex items-center gap-4 flex-wrap">
                    <label className="flex items-center gap-2 text-xs text-muted-foreground">
                        <input type="checkbox" checked={form.gst_relevant} onChange={e => setForm({...form, gst_relevant: e.target.checked})}
                            data-testid="acc-coa-gst" className="accent-primary"/>GST relevant
                    </label>
                    {isEdit && !account.is_system && (
                        <label className="flex items-center gap-2 text-xs text-muted-foreground">
                            <input type="checkbox" checked={form.is_active} onChange={e => setForm({...form, is_active: e.target.checked})}
                                data-testid="acc-coa-active" className="accent-primary"/>Active
                        </label>
                    )}
                    {!isEdit && (
                        <label className="flex items-center gap-2 text-xs text-foreground">
                            <input type="checkbox" checked={propagate} onChange={e => setPropagate(e.target.checked)}
                                data-testid="acc-coa-propagate" className="accent-primary"/>
                            Also add to every tenant&apos;s books
                        </label>
                    )}
                </div>

                {!isEdit && kind !== "account" && !form.parent_code && (
                    <div className="text-[11px] text-amber-400 border border-amber-500/30 bg-amber-500/5 px-2 py-1.5">
                        Select a parent before saving.
                    </div>
                )}

                <div className="flex items-center justify-between gap-2 pt-2 border-t border-border">
                    <div>
                        {isEdit && !account.is_system && (
                            <button type="button" onClick={handleDelete} disabled={deleting}
                                data-testid="acc-coa-delete"
                                className="inline-flex items-center gap-1.5 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-rose-500/40 text-rose-400 hover:bg-rose-500/10 disabled:opacity-50">
                                <Trash2 className="w-3 h-3"/>{deleting ? "Deleting…" : "Delete"}
                            </button>
                        )}
                    </div>
                    <div className="flex gap-2">
                        <button type="button" onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Cancel</button>
                        <button type="submit" disabled={saving || (!isEdit && kind !== "account" && !form.parent_code)}
                            data-testid="acc-coa-save"
                            className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] border border-primary text-primary hover:bg-primary/10 disabled:opacity-50">
                            {saving ? "Saving…" : (isEdit ? "Save changes" : "Create")}
                        </button>
                    </div>
                </div>
            </form>
        </div>
    );
}

function JournalPanel() {
    const { data: rows = [], reload } = useFetch("/admin/accounting/journal?limit=100");
    const ctrl = useTableControls(rows || [], {
        accessors: { amount: (e) => e.total },
    });
    const [editing, setEditing] = useState(null);
    return (
        <div data-testid="acc-journal">
            <div className="flex justify-between mb-3">
                <p className="text-xs text-muted-foreground">Every monetary movement, auto-posted. Each entry balances DR = CR. Edit narration + line memos; amounts are immutable — use Reverse + repost for corrections.</p>
                <button onClick={reload} className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><RefreshCw className="w-3 h-3"/>Refresh</button>
            </div>
            <FilterChip ctrl={ctrl} className="mb-2"/>
            <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        <tr className="border-b border-border">
                            <SortableTh field="date" ctrl={ctrl} className="text-left px-4 py-2">Date</SortableTh>
                            <SortableTh field="narration" ctrl={ctrl} className="text-left px-4 py-2">Narration</SortableTh>
                            <SortableTh field="source" ctrl={ctrl} className="text-left px-4 py-2">Source</SortableTh>
                            <SortableTh field="amount" ctrl={ctrl} className="text-right px-4 py-2" align="right">Amount</SortableTh>
                            <th className="text-right px-4 py-2"></th>
                        </tr>
                    </thead>
                    <tbody>
                        {ctrl.rows.map(e => (
                            <tr key={e.id} className={`border-b border-card ${e.reversed ? "opacity-50" : ""}`} data-testid={`acc-journal-row-${e.id}`}>
                                <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{fmtDateTime(e.date)}</td>
                                <td className="px-4 py-2 text-foreground">{e.narration} <div className="text-[10px] text-muted-foreground">{e.lines.map(l => `${l.account_code}:${l.debit ? `DR ${inr(l.debit)}` : `CR ${inr(l.credit)}`}${l.memo ? ` (${l.memo})` : ""}`).join(" · ")}</div></td>
                                <td className="px-4 py-2"><code className="text-[10px] bg-background px-1.5 py-0.5 text-primary">{e.source}</code></td>
                                <td className="px-4 py-2 text-right text-foreground">{inr(e.total)}</td>
                                <td className="px-4 py-2 text-right text-[10px]">
                                    {e.reversed ? <span className="text-rose-400">REV</span>
                                    : (
                                        <div className="inline-flex items-center gap-3">
                                            <button onClick={() => setEditing(e)}
                                                data-testid={`acc-journal-edit-${e.id}`}
                                                className="text-primary hover:underline uppercase tracking-[0.14em]">Edit</button>
                                            <button onClick={async () => {
                                                if (!window.confirm(`Reverse this journal entry?\n\n"${e.narration || "(no narration)"}"\nAmount: ${inr(e.total)}\n\nThis creates a mirror DR/CR entry that cancels the original. Audit-logged. Cannot be undone.`)) return;
                                                try {
                                                    await api.post(`/admin/accounting/journal/${e.id}/reverse`);
                                                    toast.success("Journal entry reversed");
                                                    reload();
                                                } catch (err) {
                                                    // Period-locked entries need `force=true`
                                                    if ((err?.response?.data?.detail || "").toLowerCase().includes("locked")) {
                                                        if (window.confirm("This entry falls in a locked period. Force-reverse anyway? (super-admin override, audit-logged)")) {
                                                            try {
                                                                await api.post(`/admin/accounting/journal/${e.id}/reverse?force=true`);
                                                                toast.success("Journal entry force-reversed");
                                                                reload();
                                                            } catch (err2) { toast.error(apiError(err2)); }
                                                        }
                                                    } else { toast.error(apiError(err)); }
                                                }
                                            }}
                                                data-testid={`acc-journal-reverse-${e.id}`}
                                                className="text-rose-400 hover:underline uppercase tracking-[0.14em] inline-flex items-center gap-1">
                                                <Undo2 className="w-3 h-3"/> Reverse
                                            </button>
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
            {editing && (
                <JournalEditModal entry={editing}
                    onClose={() => setEditing(null)}
                    onSaved={() => { setEditing(null); reload(); }}/>
            )}
        </div>
    );
}


function JournalEditModal({ entry, onClose, onSaved }) {
    const [narration, setNarration] = useState(entry.narration || "");
    const [memos, setMemos] = useState((entry.lines || []).map(l => l.memo || ""));
    const [saving, setSaving] = useState(false);

    const save = async () => {
        setSaving(true);
        try {
            await api.patch(`/admin/accounting/journal/${entry.id}`, {
                narration,
                line_memos: memos,
            });
            toast.success("Journal entry updated");
            onSaved?.();
        } catch (e) {
            toast.error(apiError(e));
        } finally {
            setSaving(false);
        }
    };

    return (
        <div className="fixed inset-0 z-50 bg-black/60 backdrop-blur-sm flex items-center justify-center p-4"
             onClick={onClose} data-testid="acc-journal-edit-modal">
            <div className="bg-card border border-border rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto"
                 onClick={e => e.stopPropagation()}>
                <div className="p-5 border-b border-border flex items-center justify-between">
                    <div>
                        <div className="text-sm font-medium text-foreground">Edit journal entry</div>
                        <div className="text-[10px] text-muted-foreground mt-1">{fmtDateTime(entry.date)} · <code className="text-primary">{entry.source}</code> · Total {inr(entry.total)}</div>
                    </div>
                    <button onClick={onClose} className="text-muted-foreground hover:text-foreground p-1"><X className="w-4 h-4"/></button>
                </div>
                <div className="p-5 space-y-4">
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground block mb-1">Narration</label>
                        <input value={narration}
                            onChange={e => setNarration(e.target.value)}
                            data-testid="acc-journal-edit-narration"
                            className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-sm text-foreground"
                            placeholder="Free-text description of the transaction"/>
                    </div>
                    <div>
                        <label className="text-[10px] uppercase tracking-[0.14em] text-muted-foreground block mb-1">Line memos</label>
                        <div className="space-y-2">
                            {(entry.lines || []).map((ln, i) => (
                                <div key={i} className="flex items-center gap-2 border border-border rounded-lg p-2 bg-background/50">
                                    <div className="text-[10px] font-mono text-primary shrink-0 w-14">{ln.account_code}</div>
                                    <div className="text-[10px] text-muted-foreground shrink-0 w-24">
                                        {ln.debit ? <span className="text-emerald-400">DR {inr(ln.debit)}</span> : <span className="text-rose-400">CR {inr(ln.credit)}</span>}
                                    </div>
                                    <input value={memos[i] || ""}
                                        onChange={e => setMemos(m => m.map((x, idx) => idx === i ? e.target.value : x))}
                                        data-testid={`acc-journal-edit-memo-${i}`}
                                        placeholder="Line memo"
                                        className="flex-1 bg-background border border-border focus:border-primary outline-none px-2 py-1.5 text-xs text-foreground"/>
                                </div>
                            ))}
                        </div>
                    </div>
                    <div className="text-[10px] text-muted-foreground border border-border rounded-lg p-2.5 bg-background/50">
                        <strong className="text-foreground">Note:</strong> Amounts and accounts cannot be edited (accounting integrity). To correct amounts, click <em>Reverse</em> on the row and post a fresh corrected entry.
                    </div>
                </div>
                <div className="p-5 border-t border-border flex items-center justify-end gap-2">
                    <button onClick={onClose}
                        className="text-[10px] uppercase tracking-[0.14em] px-3 py-2 border border-border text-muted-foreground hover:text-foreground rounded">
                        Cancel
                    </button>
                    <button onClick={save} disabled={saving}
                        data-testid="acc-journal-edit-save"
                        className="text-[10px] uppercase tracking-[0.14em] px-3 py-2 bg-primary text-black rounded disabled:opacity-50">
                        {saving ? "Saving…" : "Save changes"}
                    </button>
                </div>
            </div>
        </div>
    );
}

function LedgerPanel() {
    const { data: coa = [] } = useFetch("/admin/accounting/coa");
    const [code, setCode] = useState("1100");
    const { data, reload } = useFetch(`/admin/accounting/ledger/${code}`, [code]);
    return (
        <div data-testid="acc-ledger">
            <div className="flex gap-3 items-center mb-3">
                <select value={code} onChange={e => setCode(e.target.value)} data-testid="acc-ledger-acct"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs">
                    {(coa||[]).map(a => <option key={a.code} value={a.code}>{`${a.code} · ${a.name}`}</option>)}
                </select>
                <button onClick={reload} className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground">Reload</button>
                {data && <div className="ml-auto text-xs">Closing: <span className="text-primary font-semibold">{inr(data.closing_balance)}</span></div>}
            </div>
            {data && <LedgerPanelTable data={data}/>}
        </div>
    );
}

function LedgerPanelTable({ data }) {
    const ctrl = useTableControls(data.rows || [], {
        accessors: { memo: (r) => r.memo || r.narration || "" },
    });
    return (
        <>
        <FilterChip ctrl={ctrl} className="mb-2"/>
        <div className="border border-border bg-card overflow-x-auto">
            <table className="w-full text-xs">
                <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                    <tr className="border-b border-border">
                        <SortableTh field="date" ctrl={ctrl} className="text-left px-4 py-2">Date</SortableTh>
                        <SortableTh field="memo" ctrl={ctrl} className="text-left px-4 py-2">Memo</SortableTh>
                        <SortableTh field="debit" ctrl={ctrl} className="text-right px-4 py-2" align="right">DR</SortableTh>
                        <SortableTh field="credit" ctrl={ctrl} className="text-right px-4 py-2" align="right">CR</SortableTh>
                        <SortableTh field="running_balance" ctrl={ctrl} className="text-right px-4 py-2" align="right">Running</SortableTh>
                    </tr>
                </thead>
                <tbody>
                    {ctrl.rows.map((r,i) => (
                        <tr key={r.entry_id || `${r.date}-${i}`} className="border-b border-card">
                            <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{fmtDateTime(r.date)}</td>
                            <td className="px-4 py-2 text-foreground">{r.memo || r.narration}</td>
                            <td className="px-4 py-2 text-right text-emerald-400">{r.debit ? inr(r.debit) : ""}</td>
                            <td className="px-4 py-2 text-right text-rose-400">{r.credit ? inr(r.credit) : ""}</td>
                            <td className="px-4 py-2 text-right text-primary">{inr(r.running_balance)}</td>
                        </tr>
                    ))}
                </tbody>
            </table>
        </div>
        </>
    );
}

function ReportsPanel() {
    const [sub, setSub] = useState("tb");
    return (
        <div className="space-y-3">
            <div className="flex gap-2 text-[10px] uppercase tracking-[0.16em]">
                {[["tb","Trial Balance"],["pnl","P&L"],["bs","Balance Sheet"]].map(([id,label]) => (
                    <button key={id} onClick={() => setSub(id)} data-testid={`acc-reports-${id}`}
                        className={`px-3 py-1.5 border ${sub === id ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{label}</button>
                ))}
            </div>
            {sub === "tb" && <TrialBalance/>}
            {sub === "pnl" && <Pnl/>}
            {sub === "bs" && <BalanceSheet/>}
        </div>
    );
}

function TrialBalance() {
    const { data } = useFetch("/admin/accounting/reports/trial-balance");
    if (!data) return <div className="text-xs text-muted-foreground">Loading…</div>;
    return (
        <div data-testid="acc-tb">
            <div className={`text-xs mb-2 ${data.totals.balanced ? "text-emerald-400" : "text-rose-400"}`}>
                {data.totals.balanced ? "✓ Balanced" : "⚠ Unbalanced"} · DR {inr(data.totals.debit)} · CR {inr(data.totals.credit)}
            </div>
            <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground"><tr className="border-b border-border"><th className="text-left px-4 py-2">Code</th><th className="text-left px-4 py-2">Account</th><th className="text-right px-4 py-2">DR</th><th className="text-right px-4 py-2">CR</th><th className="text-right px-4 py-2">Balance</th></tr></thead>
                    <tbody>{data.rows.map(r => (
                        <tr key={r.code} className="border-b border-card"><td className="px-4 py-2 font-mono text-primary">{r.code}</td><td className="px-4 py-2 text-foreground">{r.name}</td><td className="px-4 py-2 text-right text-muted-foreground">{inr(r.debit)}</td><td className="px-4 py-2 text-right text-muted-foreground">{inr(r.credit)}</td><td className="px-4 py-2 text-right text-primary font-semibold">{inr(r.balance)}</td></tr>
                    ))}</tbody>
                </table>
            </div>
        </div>
    );
}

function Pnl() {
    const { data } = useFetch("/admin/accounting/reports/pnl");
    if (!data) return <div className="text-xs text-muted-foreground">Loading…</div>;
    return (
        <div data-testid="acc-pnl" className="grid sm:grid-cols-2 gap-3">
            <Block title={`Income (${inr(data.total_income)})`} rows={data.income}/>
            <Block title={`Expense (${inr(data.total_expense)})`} rows={data.expense}/>
            <div className="sm:col-span-2 border border-primary/40 bg-primary/5 p-3 text-sm">
                Net Profit: <strong className="text-primary text-lg">{inr(data.net_profit)}</strong>
            </div>
        </div>
    );
}

function BalanceSheet() {
    const { data } = useFetch("/admin/accounting/reports/balance-sheet");
    if (!data) return <div className="text-xs text-muted-foreground">Loading…</div>;
    return (
        <div data-testid="acc-bs" className="grid lg:grid-cols-3 gap-3">
            <Block title={`Assets (${inr(data.total_assets)})`} rows={data.assets}/>
            <Block title={`Liabilities (${inr(data.total_liabilities)})`} rows={data.liabilities}/>
            <Block title={`Equity (${inr(data.total_equity)})`} rows={data.equity}/>
            <div className={`lg:col-span-3 border p-3 text-sm ${data.is_balanced ? "border-emerald-400/40 bg-emerald-400/5 text-emerald-400" : "border-rose-400/40 bg-rose-400/5 text-rose-400"}`}>
                {data.is_balanced ? "✓ Balanced" : "⚠ Unbalanced"} · A = L+E → {inr(data.total_assets)} = {inr(data.total_liabilities + data.total_equity)}
            </div>
        </div>
    );
}

function Block({ title, rows = [] }) {
    return (
        <div className="border border-border bg-card">
            <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-[0.16em] text-primary">{title}</div>
            <table className="w-full text-xs">
                <tbody>{rows.map(r => (
                    <tr key={r.code} className="border-b border-card"><td className="px-3 py-1.5 font-mono text-muted-foreground">{r.code}</td><td className="px-3 py-1.5 text-foreground">{r.name}</td><td className="px-3 py-1.5 text-right text-primary">{inr(r.amount)}</td></tr>
                ))}</tbody>
            </table>
        </div>
    );
}

function InvoicesPanel() {
    const { data: rows = [], reload } = useFetch("/admin/accounting/invoices?limit=50");
    const { data: shareEvents = {} } = useFetch("/admin/accounting/invoices/share-events/bulk");
    const [showNew, setShowNew] = useState(false);
    const [shareFor, setShareFor] = useState(null);
    const relTime = (iso) => {
        if (!iso) return null;
        const diff = (Date.now() - new Date(iso).getTime()) / 1000;
        if (diff < 60) return "just now";
        if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
        if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
        if (diff < 604800) return `${Math.floor(diff / 86400)}d ago`;
        return fmtDate(iso);
    };
    const ctrl = useTableControls(rows || [], {
        accessors: { customer: (r) => r.customer_name, date: (r) => r.issue_date },
    });
    return (
        <div data-testid="acc-invoices">
            <div className="flex justify-between mb-3">
                <p className="text-xs text-muted-foreground">GST-compliant invoices. Issuing one auto-posts a journal entry. Mark-paid auto-posts the receipt.</p>
                <button onClick={() => setShowNew(true)} data-testid="acc-invoice-new"
                    className="inline-flex items-center gap-1.5 border border-primary text-primary hover:bg-primary/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] font-medium"><Plus className="w-3 h-3"/>New invoice</button>
            </div>
            <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground"><tr className="border-b border-border"><th className="text-left px-4 py-2">#</th><th className="text-left px-4 py-2">Customer</th><th className="text-right px-4 py-2">Subtotal</th><th className="text-right px-4 py-2">Tax</th><th className="text-right px-4 py-2">Total</th><th className="text-center px-4 py-2">Status</th><th></th></tr></thead>
                    <tbody>{ctrl.rows.map(r => (
                        <tr key={r.id} className="border-b border-card hover:bg-muted/50 cursor-pointer" data-testid={`acc-invoice-row-${r.invoice_number}`}
                            onClick={(e) => { if (e.target.closest('button,a')) return; setShareFor(r); }}>
                            <td className="px-4 py-2 font-mono text-primary">
                                {r.invoice_number}
                                {(() => {
                                    const ev = shareEvents[r.id];
                                    if (!ev) return null;
                                    const lastEv = ev.last_view || ev.last_pdf;
                                    if (!lastEv) return null;
                                    const total = (ev.view_count || 0) + (ev.pdf_count || 0);
                                    return (
                                        <span className="ml-2 inline-flex items-center gap-1 text-[9px] text-emerald-400 uppercase tracking-wider"
                                            data-testid={`acc-invoice-read-${r.invoice_number}`}
                                            title={`${ev.view_count || 0} views · ${ev.pdf_count || 0} PDF downloads`}>
                                            <span className="w-1 h-1 rounded-full bg-emerald-400"></span>
                                            Read · {relTime(lastEv)}{total > 1 ? ` · ${total}` : ""}
                                        </span>
                                    );
                                })()}
                            </td>
                            <td className="px-4 py-2 text-foreground">{r.customer_name}<div className="text-[10px] text-muted-foreground">{fmtDateTime(r.issue_date)}{r.inter_state && " · inter-state"}</div></td>
                            <td className="px-4 py-2 text-right text-muted-foreground">{inr(r.subtotal)}</td>
                            <td className="px-4 py-2 text-right text-muted-foreground">{inr(r.total_tax)}</td>
                            <td className="px-4 py-2 text-right text-foreground font-semibold">{inr(r.grand_total)}</td>
                            <td className="px-4 py-2 text-center text-[10px] uppercase tracking-[0.16em] {r.status === 'paid' ? 'text-emerald-400' : 'text-primary'}">{r.status}</td>
                            <td className="px-4 py-2 text-right whitespace-nowrap">
                                <button onClick={() => setShareFor(r)} data-testid={`acc-invoice-share-${r.invoice_number}`}
                                    className="text-[10px] uppercase tracking-[0.16em] text-primary hover:underline inline-flex items-center gap-1 mr-3"><Share2 className="w-3 h-3"/>Share</button>
                                <button onClick={() => authDownload(`/admin/accounting/invoices/${r.id}/pdf`, { filename: `invoice-${r.invoice_number}.pdf` })}
                                    data-testid={`acc-invoice-pdf-${r.invoice_number}`}
                                    className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1 mr-3"><Download className="w-3 h-3"/>PDF</button>
                                {r.status !== "paid" && <button onClick={async () => { try { await api.post(`/admin/accounting/invoices/${r.id}/mark-paid`); toast.success("Marked paid"); reload(); } catch(e){toast.error(apiError(e));} }}
                                    data-testid={`acc-invoice-paid-${r.invoice_number}`}
                                    className="text-[10px] uppercase tracking-[0.16em] text-emerald-400 hover:underline">Mark paid</button>}
                            </td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>
            {showNew && <NewInvoiceModal onClose={() => setShowNew(false)} onCreated={() => { setShowNew(false); reload(); }}/>}
            <InvoiceShareModal scope="platform" invoice={shareFor} open={!!shareFor} onClose={() => setShareFor(null)}/>
        </div>
    );
}

function NewInvoiceModal({ onClose, onCreated }) {
    const [form, setForm] = useState({ customer_name: "", customer_gstin: "", customer_state_code: "27",
                                         lines: [{ description: "", hsn_sac: "998213", quantity: 1, rate: 0, gst_rate_pct: 18 }] });
    const [saving, setSaving] = useState(false);
    const submit = async () => {
        setSaving(true);
        try { const { data } = await api.post("/admin/accounting/invoices", form); toast.success(`Invoice ${data.invoice_number} created`); onCreated(); }
        catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
    };
    const updLine = (i, k, v) => { const ls = [...form.lines]; ls[i] = { ...ls[i], [k]: v }; setForm({ ...form, lines: ls }); };
    return (
        <div className="fixed inset-0 bg-background/70 z-50 flex items-center justify-center p-4" onClick={onClose}>
            <div onClick={e => e.stopPropagation()} className="bg-card border border-border max-w-2xl w-full p-5" data-testid="acc-new-invoice">
                <h3 className="text-lg text-foreground mb-3">New invoice</h3>
                <div className="grid grid-cols-3 gap-2">
                    <Inp label="Customer name *" v={form.customer_name} on={v => setForm({ ...form, customer_name: v })} tid="acc-inv-cust"/>
                    <Inp label="Customer GSTIN" v={form.customer_gstin} on={v => setForm({ ...form, customer_gstin: v.toUpperCase() })} tid="acc-inv-gstin"/>
                    <Inp label="State code" v={form.customer_state_code} on={v => setForm({ ...form, customer_state_code: v })} tid="acc-inv-state"/>
                </div>
                <div className="mt-3">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">Line items</div>
                    {form.lines.map((ln, i) => (
                        <div key={i} className="grid grid-cols-12 gap-1 mb-1">
                            <input placeholder="Description" value={ln.description} onChange={e => updLine(i, "description", e.target.value)} data-testid={`acc-inv-line-desc-${i}`} className="col-span-5 bg-background border border-border focus:border-primary outline-none px-2 py-1.5 text-xs"/>
                            <input placeholder="HSN" value={ln.hsn_sac} onChange={e => updLine(i, "hsn_sac", e.target.value)} className="col-span-2 bg-background border border-border outline-none px-2 py-1.5 text-xs"/>
                            <input placeholder="Qty" type="number" value={ln.quantity} onChange={e => updLine(i, "quantity", parseFloat(e.target.value))} className="col-span-1 bg-background border border-border outline-none px-2 py-1.5 text-xs"/>
                            <input placeholder="Rate ₹" type="number" value={ln.rate} onChange={e => updLine(i, "rate", parseFloat(e.target.value))} data-testid={`acc-inv-line-rate-${i}`} className="col-span-2 bg-background border border-border outline-none px-2 py-1.5 text-xs"/>
                            <input placeholder="GST %" type="number" value={ln.gst_rate_pct} onChange={e => updLine(i, "gst_rate_pct", parseFloat(e.target.value))} className="col-span-2 bg-background border border-border outline-none px-2 py-1.5 text-xs"/>
                        </div>
                    ))}
                    <button onClick={() => setForm({ ...form, lines: [...form.lines, { description: "", hsn_sac: "", quantity: 1, rate: 0, gst_rate_pct: 18 }] })}
                        className="text-[10px] uppercase tracking-[0.16em] text-primary hover:underline">+ Add line</button>
                </div>
                <div className="flex justify-end gap-2 mt-4">
                    <button onClick={onClose} className="px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] text-muted-foreground">Cancel</button>
                    <button onClick={submit} disabled={saving} data-testid="acc-inv-create"
                        className="px-4 py-1.5 bg-primary text-primary-foreground hover:bg-primary disabled:opacity-30 text-[10px] uppercase tracking-[0.16em] font-medium">{saving ? "Creating…" : "Create"}</button>
                </div>
            </div>
        </div>
    );
}

function GstPanel() {
    const { data, reload } = useFetch("/admin/accounting/gst-config");
    const [form, setForm] = useState({});
    useEffect(() => { if (data) setForm(data); }, [data]);

    const enabled = Boolean(form.gst_enabled);

    const toggle = async () => {
        const next = { ...form, gst_enabled: !enabled };
        setForm(next);
        try {
            await api.put("/admin/accounting/gst-config", next);
            toast.success(!enabled ? "GST enabled — platform invoices will include tax." : "GST disabled — platform invoices will not include tax.");
            reload();
        } catch (e) { toast.error(apiError(e)); reload(); }
    };

    const save = async () => {
        try {
            await api.put("/admin/accounting/gst-config", form);
            toast.success("GST config saved"); reload();
        } catch (e) { toast.error(apiError(e)); }
    };

    if (!data) return <div className="text-xs text-muted-foreground">Loading…</div>;
    return (
        <div className="space-y-4 max-w-xl" data-testid="acc-gst">
            {/* ── Enable / Disable toggle ───────────────────────────────── */}
            <div className={`flex items-center justify-between px-4 py-3 border rounded ${enabled ? "border-green-500/40 bg-green-500/5" : "border-border bg-muted/30"}`}>
                <div>
                    <div className="text-sm font-medium text-foreground">GST Registration</div>
                    <div className="text-xs text-muted-foreground mt-0.5">
                        {enabled
                            ? "GST is active — platform invoices will include tax lines."
                            : "GST is disabled — invoices created without tax. Enable once InnoAim is GST-registered."}
                    </div>
                </div>
                <button
                    onClick={toggle}
                    data-testid="acc-gst-toggle"
                    className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none ${enabled ? "bg-green-500" : "bg-muted-foreground/40"}`}
                >
                    <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${enabled ? "translate-x-6" : "translate-x-1"}`} />
                </button>
            </div>

            {/* ── GST fields — greyed out when disabled ─────────────────── */}
            <div className={`space-y-3 transition-opacity ${enabled ? "opacity-100" : "opacity-40 pointer-events-none"}`}>
                <Inp label="Company GSTIN" v={form.company_gstin||""} on={v => setForm({ ...form, company_gstin: v.toUpperCase() })} tid="acc-gst-gstin" hint="15-char format with checksum validation."/>
                <Inp label="Company state code" v={form.company_state_code||""} on={v => setForm({ ...form, company_state_code: v })} tid="acc-gst-state" hint="2-digit (27 = Maharashtra, 29 = Karnataka, etc)."/>
                <Inp label="Default GST rate %" v={form.default_rate_pct||""} on={v => setForm({ ...form, default_rate_pct: parseFloat(v) })} tid="acc-gst-rate"/>
            </div>

            {/* ── Always editable ───────────────────────────────────────── */}
            <div className="space-y-3">
                <Inp label="Invoice prefix" v={form.invoice_prefix||""} on={v => setForm({ ...form, invoice_prefix: v })} tid="acc-gst-prefix"/>
                <Inp label="TDS rate %" v={form.tds_rate_pct||""} on={v => setForm({ ...form, tds_rate_pct: parseFloat(v) })} tid="acc-gst-tds"/>
            </div>

            <button onClick={save} data-testid="acc-gst-save"
                className="px-4 py-2 bg-primary text-primary-foreground hover:bg-primary text-[10px] uppercase tracking-[0.16em] font-medium">Save GST config</button>
        </div>
    );
}

function PeriodsPanel() {
    const { data: rows = [], reload } = useFetch("/admin/accounting/periods");
    const act = async (key, action) => {
        try { await api.post(`/admin/accounting/periods/${key}/${action}`); toast.success(action); reload(); }
        catch (e) { toast.error(apiError(e)); }
    };
    const ctrl = useTableControls(rows || []);
    return (
        <div data-testid="acc-periods">
            <p className="text-xs text-muted-foreground mb-3">Once closed, no entries are allowed in a period unless super admin uses <code className="bg-background px-1">force=true</code> (audit-logged).</p>
            <FilterChip ctrl={ctrl} className="mb-2"/>
            <div className="border border-border bg-card">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground"><tr className="border-b border-border">
                        <SortableTh field="period" ctrl={ctrl} className="text-left px-4 py-2">Period</SortableTh>
                        <SortableTh field="status" ctrl={ctrl} className="text-left px-4 py-2">Status</SortableTh>
                        <SortableTh field="closed_at" ctrl={ctrl} className="text-left px-4 py-2">Closed at</SortableTh>
                        <th></th>
                    </tr></thead>
                    <tbody>{ctrl.rows.map(p => (
                        <tr key={p.period} className="border-b border-card">
                            <td className="px-4 py-2 font-mono text-primary">{p.period}</td>
                            <td className="px-4 py-2"><span className={p.status === "closed" ? "text-rose-400" : "text-emerald-400"}>{p.status}</span></td>
                            <td className="px-4 py-2 text-muted-foreground">{p.closed_at ? fmtDateTime(p.closed_at) : "—"}</td>
                            <td className="px-4 py-2 text-right">
                                {p.status === "open"
                                    ? <button onClick={() => act(p.period, "close")} data-testid={`acc-period-close-${p.period}`} className="text-[10px] uppercase tracking-[0.16em] text-rose-400 hover:underline">Close</button>
                                    : <button onClick={() => act(p.period, "reopen")} data-testid={`acc-period-reopen-${p.period}`} className="text-[10px] uppercase tracking-[0.16em] text-emerald-400 hover:underline">Reopen</button>}
                            </td>
                        </tr>
                    ))}</tbody>
                </table>
            </div>
        </div>
    );
}

function HealthPanel() {
    const { data } = useFetch("/admin/accounting/health");
    if (!data) return <div className="text-xs text-muted-foreground">Loading…</div>;
    return (
        <div data-testid="acc-health" className="space-y-3 max-w-2xl">
            <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                <Stat label="Total entries" value={data.summary.total_entries}/>
                <Stat label="Open periods" value={data.summary.open_periods}/>
                <Stat label="Closed periods" value={data.summary.closed_periods}/>
            </div>
            <div className="border border-border bg-card">
                {data.findings.map((f,i) => (
                    <div key={f.code || i} className={`px-4 py-3 border-b border-card flex items-start gap-3 ${
                        f.severity === "critical" ? "text-rose-400" : f.severity === "warning" ? "text-amber-400" : "text-emerald-400"
                    }`}>
                        <span className="text-[10px] uppercase tracking-[0.16em] w-20 shrink-0">{f.severity}</span>
                        <code className="text-[10px] bg-background px-1.5 py-0.5 shrink-0">{f.code}</code>
                        <span className="text-xs flex-1">{f.message}</span>
                    </div>
                ))}
            </div>
        </div>
    );
}

function Stat({ label, value }) {
    return <div className="border border-border bg-card p-3"><div className="text-2xl font-semibold text-primary">{value}</div><div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mt-1">{label}</div></div>;
}

function Inp({ label, v, on, hint, tid }) {
    return (
        <label className="block">
            <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-1">{label}</div>
            <input value={v ?? ""} onChange={e => on(e.target.value)} data-testid={tid} className="w-full bg-background border border-border focus:border-primary outline-none px-3 py-2 text-xs"/>
            {hint && <div className="text-[10px] text-muted-foreground mt-1">{hint}</div>}
        </label>
    );
}


// ──────────────────────────────  BANK RECONCILIATION  ───────────────────────────
function ReconPanel() {
    const [stmtId, setStmtId] = useState("");
    const [filter, setFilter] = useState("all");
    const { data: statements = [], reload: reloadStmts } = useFetch("/admin/accounting/recon/statements");
    const { data: txns = [], reload: reloadTxns } = useFetch(
        `/admin/accounting/recon/txns?filter=${filter}${stmtId ? `&statement_id=${stmtId}` : ""}`,
        [filter, stmtId]
    );
    const [busy, setBusy] = useState(false);
    const onUpload = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;
        const fd = new FormData(); fd.append("file", file);
        setBusy(true);
        try {
            const { data } = await api.post("/admin/accounting/recon/upload", fd, {
                headers: { "Content-Type": "multipart/form-data" } });
            toast.success(`Parsed ${data.txn_count} txns from ${file.name}`);
            reloadStmts(); reloadTxns();
            e.target.value = "";
        } catch (err) { toast.error(apiError(err)); } finally { setBusy(false); }
    };
    const autoMatch = async () => {
        setBusy(true);
        try {
            const { data } = await api.post(`/admin/accounting/recon/auto-match${stmtId ? `?statement_id=${stmtId}` : ""}`);
            toast.success(`${data.matched} matched · ${data.ambiguous} ambiguous · ${data.scanned} scanned`);
            reloadTxns();
        } catch (e) { toast.error(apiError(e)); } finally { setBusy(false); }
    };
    const txnsCtrl = useTableControls(txns || [], {
        accessors: {
            description: (t) => `${t.description || ""} ${t.ref || ""}`.trim(),
            status: (t) => t.matched ? "matched" : t.ambiguous ? "ambiguous" : "unmatched",
        },
    });
    return (
        <div data-testid="acc-recon" className="space-y-4">
            <div className="grid lg:grid-cols-3 gap-3">
                <div className="border border-border bg-card p-4 lg:col-span-2">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Upload bank statement</div>
                    <p className="text-xs text-muted-foreground mb-3">CSV or XLSX. Auto-detects columns Date, Debit/Credit/Amount, Description, Balance. Max 5 MB.</p>
                    <label className="inline-flex items-center gap-2 border border-dashed border-primary/40 hover:border-primary px-4 py-3 cursor-pointer transition-colors" data-testid="acc-recon-upload-label">
                        <Upload className="w-4 h-4 text-primary"/>
                        <span className="text-xs text-primary">{busy ? "Working…" : "Choose file"}</span>
                        <input type="file" accept=".csv,.xlsx,.xls" onChange={onUpload} className="hidden" data-testid="acc-recon-upload-input" disabled={busy}/>
                    </label>
                </div>
                <div className="border border-border bg-card p-4">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground mb-2">Auto-match against journal</div>
                    <p className="text-xs text-muted-foreground mb-3">Matches by ±₹1 amount + ±3 days window. Ambiguous candidates need human pick.</p>
                    <button onClick={autoMatch} disabled={busy} data-testid="acc-recon-automatch"
                        className="border border-primary text-primary hover:bg-primary/10 px-3 py-1.5 text-[10px] uppercase tracking-[0.16em] font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                        <Sparkles className="w-3 h-3"/>{busy ? "Matching…" : "Run auto-match"}
                    </button>
                </div>
            </div>

            <div className="flex flex-wrap gap-2 items-center text-[10px] uppercase tracking-[0.16em]">
                <select value={stmtId} onChange={e => setStmtId(e.target.value)} data-testid="acc-recon-stmt"
                    className="bg-background border border-border outline-none px-2 py-1.5 text-xs">
                    <option value="">All statements</option>
                    {(statements||[]).map(s => <option key={s.id} value={s.id}>{`${s.filename} · ${s.row_count} rows`}</option>)}
                </select>
                {["all","matched","unmatched","ambiguous"].map(f => (
                    <button key={f} onClick={() => setFilter(f)} data-testid={`acc-recon-filter-${f}`}
                        className={`px-3 py-1.5 border ${filter === f ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{f}</button>
                ))}
            </div>

            <FilterChip ctrl={txnsCtrl}/>
            <div className="border border-border bg-card overflow-x-auto">
                <table className="w-full text-xs">
                    <thead className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground">
                        <tr className="border-b border-border">
                            <SortableTh field="date" ctrl={txnsCtrl} className="text-left px-4 py-2">Date</SortableTh>
                            <SortableTh field="description" ctrl={txnsCtrl} className="text-left px-4 py-2">Description</SortableTh>
                            <SortableTh field="debit" ctrl={txnsCtrl} className="text-right px-4 py-2" align="right">Debit</SortableTh>
                            <SortableTh field="credit" ctrl={txnsCtrl} className="text-right px-4 py-2" align="right">Credit</SortableTh>
                            <SortableTh field="status" ctrl={txnsCtrl} className="text-center px-4 py-2" align="center">Status</SortableTh>
                            <th className="text-right px-4 py-2">Actions</th>
                        </tr>
                    </thead>
                    <tbody>
                        {txnsCtrl.rows.length === 0 && <tr><td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">No bank transactions. Upload a statement to begin.</td></tr>}
                        {txnsCtrl.rows.map(t => (
                            <tr key={t.id} className="border-b border-card" data-testid={`acc-recon-row-${t.id}`}>
                                <td className="px-4 py-2 text-muted-foreground whitespace-nowrap">{fmtDateTime(t.date)}</td>
                                <td className="px-4 py-2 text-foreground">{t.description || "—"}{t.ref && <div className="text-[10px] text-muted-foreground">ref: {t.ref}</div>}</td>
                                <td className="px-4 py-2 text-right text-rose-400">{t.debit ? inr(t.debit) : ""}</td>
                                <td className="px-4 py-2 text-right text-emerald-400">{t.credit ? inr(t.credit) : ""}</td>
                                <td className="px-4 py-2 text-center text-[10px] uppercase tracking-[0.16em]">
                                    {t.matched
                                        ? <span className="text-emerald-400">✓ matched{t.manual && " (manual)"}</span>
                                        : t.ambiguous
                                            ? <span className="text-amber-400">⚠ {t.ambiguous_candidates?.length || 0} candidates</span>
                                            : <span className="text-muted-foreground">unmatched</span>}
                                </td>
                                <td className="px-4 py-2 text-right text-[10px] uppercase tracking-[0.14em] whitespace-nowrap">
                                    {t.matched ? (
                                        <button onClick={async () => {
                                            if (!window.confirm("Unmatch this bank txn from its journal entry?")) return;
                                            try {
                                                await api.post(`/admin/accounting/recon/txn/${t.id}/unmatch`);
                                                toast.success("Unmatched");
                                                reloadTxns();
                                            } catch (e) { toast.error(apiError(e)); }
                                        }}
                                        data-testid={`acc-recon-unmatch-${t.id}`}
                                        className="text-rose-400 hover:underline inline-flex items-center gap-1">
                                            <X className="w-3 h-3"/> Unmatch
                                        </button>
                                    ) : (
                                        <button onClick={async () => {
                                            let choice = "";
                                            if (t.ambiguous && (t.ambiguous_candidates || []).length > 0) {
                                                const list = t.ambiguous_candidates.map((c, i) => `${i + 1}. ${c.id} · ${c.narration || "(no narration)"} · ${inr(c.total)}`).join("\n");
                                                choice = window.prompt(`Which candidate to match?\n\n${list}\n\nEnter row number (1-${t.ambiguous_candidates.length}) or paste journal entry id:`, "1");
                                                if (!choice) return;
                                                const n = parseInt(choice, 10);
                                                if (!isNaN(n) && n >= 1 && n <= t.ambiguous_candidates.length) {
                                                    choice = t.ambiguous_candidates[n - 1].id;
                                                }
                                            } else {
                                                choice = window.prompt("Paste the journal entry ID to match this bank txn to:", "");
                                                if (!choice) return;
                                            }
                                            try {
                                                await api.post(`/admin/accounting/recon/txn/${t.id}/match`, { journal_entry_id: choice.trim() });
                                                toast.success("Matched");
                                                reloadTxns();
                                            } catch (e) { toast.error(apiError(e)); }
                                        }}
                                        data-testid={`acc-recon-match-${t.id}`}
                                        className="text-primary hover:underline inline-flex items-center gap-1">
                                            <FileCheck2 className="w-3 h-3"/> Match
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

// CA Pack dispatch card — admin can send-now (all/single tenant) + fan-out + view history
function CAPackDispatchCard({ fy }) {
    const [history, setHistory] = useState(null);
    const [busy, setBusy] = useState(null);
    const [open, setOpen] = useState(false);

    const loadHistory = useCallback(async () => {
        try {
            const { data } = await api.get("/admin/ca-pack/history?limit=25");
            setHistory(data || []);
        } catch (e) { toast.error(apiError(e)); }
    }, []);

    useEffect(() => { if (open) loadHistory(); }, [open, loadHistory]);

    const sendNow = async ({ tenantId = null, label }) => {
        if (!window.confirm(`Send ${label} CA Pack for FY ${fy}?\n\nThis will build the pack and email it now. Force=true.`)) return;
        setBusy(label);
        try {
            const { data } = await api.post("/admin/ca-pack/send-now", { fy, force: true, tenant_id: tenantId });
            toast.success(`${label} sent · ${data?.recipients_count || 0} recipient(s)`);
            loadHistory();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    };

    const fanOut = async () => {
        if (!window.confirm(`Fan-out CA Pack for FY ${fy} to EVERY tenant with auto_ca_pack_on_apr_5 enabled?\n\nMimics the Apr-5 cron. Force=true.`)) return;
        setBusy("fan-out");
        try {
            const { data } = await api.post("/admin/ca-pack/fan-out", { fy, force: true });
            toast.success(`Fan-out complete · ${data?.tenants_processed || 0} tenant(s)`);
            loadHistory();
        } catch (e) { toast.error(apiError(e)); }
        finally { setBusy(null); }
    };

    return (
        <div className="border border-border bg-card p-4" data-testid="acc-capack-dispatch">
            <div className="flex flex-wrap items-center justify-between gap-3">
                <div>
                    <div className="text-xs font-medium text-foreground">CA Pack · email dispatch</div>
                    <div className="text-[11px] text-muted-foreground mt-0.5">Trigger platform-wide or tenant CA-Pack emails, and audit history of every send.</div>
                </div>
                <div className="flex flex-wrap gap-2">
                    <button onClick={() => sendNow({ tenantId: null, label: "Platform-wide" })}
                        disabled={!!busy}
                        data-testid="capack-send-platform"
                        className="text-[10px] uppercase tracking-[0.14em] border border-primary text-primary hover:bg-primary/10 px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1">
                        <Mail className="w-3 h-3"/> {busy === "Platform-wide" ? "Sending…" : "Email platform pack"}
                    </button>
                    <button onClick={() => {
                            const tid = window.prompt("Tenant ObjectId to send CA Pack to (firm owner + accountants):", "");
                            if (tid) sendNow({ tenantId: tid.trim(), label: `Tenant ${tid.trim().slice(-6)}` });
                        }}
                        disabled={!!busy}
                        data-testid="capack-send-tenant"
                        className="text-[10px] uppercase tracking-[0.14em] border border-border hover:border-primary px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1">
                        <Mail className="w-3 h-3"/> Email one tenant
                    </button>
                    <button onClick={fanOut} disabled={!!busy} data-testid="capack-fanout"
                        className="text-[10px] uppercase tracking-[0.14em] border border-amber-500/50 text-amber-500 hover:bg-amber-500/10 px-3 py-1.5 disabled:opacity-50 inline-flex items-center gap-1">
                        <Send className="w-3 h-3"/> {busy === "fan-out" ? "Fanning out…" : "Fan-out (all opted)"}
                    </button>
                    <button onClick={() => setOpen(o => !o)} data-testid="capack-history-toggle"
                        className="text-[10px] uppercase tracking-[0.14em] border border-border hover:border-primary px-3 py-1.5 inline-flex items-center gap-1">
                        <History className="w-3 h-3"/> {open ? "Hide history" : "Show history"}
                    </button>
                </div>
            </div>
            {open && (
                <div className="mt-3 border-t border-border pt-3 overflow-x-auto">
                    {!history ? <div className="text-[11px] text-muted-foreground">Loading…</div>
                    : history.length === 0 ? <div className="text-[11px] text-muted-foreground">No CA Pack sends yet.</div>
                    : (
                        <table className="w-full text-[11px]" data-testid="capack-history-table">
                            <thead className="text-[9px] uppercase tracking-[0.14em] text-muted-foreground">
                                <tr className="border-b border-border">
                                    <th className="text-left px-2 py-1.5">Sent at</th>
                                    <th className="text-left px-2 py-1.5">FY</th>
                                    <th className="text-left px-2 py-1.5">Scope</th>
                                    <th className="text-left px-2 py-1.5">Recipients</th>
                                    <th className="text-left px-2 py-1.5">Status</th>
                                    <th className="text-left px-2 py-1.5">Trigger</th>
                                </tr>
                            </thead>
                            <tbody>
                                {history.map(row => (
                                    <tr key={row.id} className="border-b border-card" data-testid={`capack-history-${row.id}`}>
                                        <td className="px-2 py-1.5 text-muted-foreground whitespace-nowrap">{fmtDateTime(row.sent_at)}</td>
                                        <td className="px-2 py-1.5 font-mono">{row.fy || "—"}</td>
                                        <td className="px-2 py-1.5">{row.tenant_id ? <code className="text-[10px] bg-background px-1">{row.tenant_id.slice(-6)}</code> : <span className="text-primary">platform</span>}</td>
                                        <td className="px-2 py-1.5 text-muted-foreground">{row.recipients_count} · {(row.recipients || []).slice(0, 2).join(", ")}{row.recipients_count > 2 ? "…" : ""}</td>
                                        <td className="px-2 py-1.5">
                                            <span className={row.status === "sent" ? "text-emerald-400" : row.status === "failed" ? "text-rose-400" : "text-muted-foreground"}>{row.status}</span>
                                            {(row.errors || []).length > 0 && <span className="ml-1 text-rose-400" title={row.errors.join("\n")}>·err</span>}
                                        </td>
                                        <td className="px-2 py-1.5 text-muted-foreground">{row.trigger || "—"}</td>
                                    </tr>
                                ))}
                            </tbody>
                        </table>
                    )}
                </div>
            )}
        </div>
    );
}



// ─────────────────────────────  GST RETURNS  ─────────────────────────────────
function GstrPanel() {
    const today = new Date();
    const [period, setPeriod] = useState(`${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, "0")}`);
    const [tab, setTab] = useState("gstr1");
    const { data, reload } = useFetch(`/admin/accounting/${tab}?period=${period}`, [tab, period]);
    // Indian FY: if month >= 4 → FY starts this year; else previous year.
    const fyStart = (today.getMonth() + 1) >= 4 ? today.getFullYear() : today.getFullYear() - 1;
    const defaultFY = `${fyStart}-${String((fyStart + 1) % 100).padStart(2, "0")}`;
    const [fy, setFy] = useState(defaultFY);
    const [packing, setPacking] = useState(false);
    const downloadCAPack = async () => {
        setPacking(true);
        try {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/accounting/ca-pack?fy=${fy}`;
            const res = await fetch(url, { credentials: "include" });
            if (!res.ok) {
                const err = await res.json().catch(() => ({}));
                throw new Error(err.detail || `HTTP ${res.status}`);
            }
            const blob = await res.blob();
            const a = document.createElement("a");
            a.href = URL.createObjectURL(blob);
            a.download = `Law-OS-CA-Pack-FY${fy}.zip`;
            document.body.appendChild(a); a.click(); a.remove();
            toast.success(`Downloaded CA pack for FY ${fy}`);
        } catch (e) { toast.error(e.message || "Failed to build CA pack"); }
        finally { setPacking(false); }
    };
    return (
        <div data-testid="acc-gstr" className="space-y-4">
            <div className="border border-primary/30 bg-gradient-to-r from-[#1A1505] to-card p-4 flex flex-wrap items-center gap-3" data-testid="acc-ca-pack-card">
                <div className="flex-1 min-w-[200px]">
                    <div className="text-[10px] uppercase tracking-[0.16em] text-primary">Year-end CA pack · one-click handoff</div>
                    <p className="text-xs text-muted-foreground leading-relaxed mt-1">
                        Bundle 12 monthly GSTR-1 CSVs + 12 GSTR-3B JSONs + Trial Balance + P&amp;L + Balance Sheet + every invoice PDF for the Indian financial year into one zip. Hand it to your CA in March — done.
                    </p>
                </div>
                <input type="text" value={fy} onChange={e => setFy(e.target.value)}
                    placeholder="2025-26" pattern="^\d{4}-\d{2}$"
                    data-testid="acc-ca-pack-fy"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-1.5 text-xs w-24 text-center font-mono"/>
                <button onClick={downloadCAPack} disabled={packing} data-testid="acc-ca-pack-download"
                    className="border border-primary bg-primary/10 text-primary hover:bg-primary/20 px-4 py-2 text-[10px] uppercase tracking-[0.16em] font-medium inline-flex items-center gap-1.5 disabled:opacity-50">
                    {packing ? <RefreshCw className="w-3.5 h-3.5 animate-spin"/> : <Download className="w-3.5 h-3.5"/>}
                    {packing ? "Building…" : "Download CA Pack"}
                </button>
            </div>

            <CAPackDispatchCard fy={fy}/>

            <div className="flex flex-wrap items-center gap-3">
                {[["gstr1","GSTR-1"],["gstr3b","GSTR-3B"]].map(([id,label]) => (
                    <button key={id} onClick={() => setTab(id)} data-testid={`acc-gstr-${id}`}
                        className={`px-3 py-1.5 border text-[10px] uppercase tracking-[0.16em] ${tab === id ? "border-primary text-primary" : "border-border text-muted-foreground hover:text-foreground"}`}>{label}</button>
                ))}
                <input type="month" value={period} onChange={e => setPeriod(e.target.value)} data-testid="acc-gstr-period"
                    className="bg-background border border-border focus:border-primary outline-none px-3 py-1.5 text-xs"/>
                <button onClick={reload} className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><RefreshCw className="w-3 h-3"/>Refresh</button>
                <button onClick={() => authDownload(`/admin/accounting/${tab}?period=${period}&format=json`, { filename: `${tab}-${period}.json` })}
                    data-testid="acc-gstr-json-dl"
                    className="text-[10px] uppercase tracking-[0.16em] text-muted-foreground hover:text-foreground inline-flex items-center gap-1"><Download className="w-3 h-3"/>JSON</button>
                {tab === "gstr1" && (
                    <button onClick={() => authDownload(`/admin/accounting/gstr1?period=${period}&format=csv`, { filename: `gstr1-${period}.csv` })}
                        data-testid="acc-gstr-csv-dl"
                        className="text-[10px] uppercase tracking-[0.16em] text-primary hover:underline inline-flex items-center gap-1"><Download className="w-3 h-3"/>CSV (portal-ready)</button>
                )}
            </div>

            {!data && <div className="text-xs text-muted-foreground">Loading…</div>}
            {data && tab === "gstr1" && <Gstr1View data={data}/>}
            {data && tab === "gstr3b" && <Gstr3bView data={data}/>}
        </div>
    );
}

function Gstr1View({ data }) {
    const s = data.doc_summary || {};
    return (
        <div className="space-y-4">
            <div className="grid sm:grid-cols-4 gap-3">
                <Stat label="Invoices" value={s.total_invoices ?? 0}/>
                <Stat label="Taxable" value={inr(s.total_taxable_value)}/>
                <Stat label="Total Tax" value={inr((s.total_cgst||0) + (s.total_sgst||0) + (s.total_igst||0))}/>
                <Stat label="Grand" value={inr(s.total_grand)}/>
            </div>
            <div className="grid lg:grid-cols-2 gap-3">
                <div className="border border-border bg-card">
                    <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-[0.16em] text-primary">B2B (registered) · {data.b2b?.length || 0} customers</div>
                    <div className="max-h-72 overflow-y-auto">
                        {(data.b2b || []).map((blk, i) => (
                            <div key={blk.customer_gstin || i} className="px-3 py-2 border-b border-card text-xs">
                                <div className="font-mono text-[10px] text-primary">{blk.customer_gstin}</div>
                                <div className="text-[10px] text-muted-foreground">{blk.invoices.length} invoice(s) · ₹{blk.invoices.reduce((a, x) => a + x.grand_total, 0).toLocaleString("en-IN")}</div>
                            </div>
                        ))}
                        {!data.b2b?.length && <div className="px-3 py-4 text-xs text-muted-foreground">No B2B invoices.</div>}
                    </div>
                </div>
                <div className="border border-border bg-card">
                    <div className="px-3 py-2 border-b border-border text-[10px] uppercase tracking-[0.16em] text-primary">HSN Summary · {data.hsn_summary?.length || 0} codes</div>
                    <div className="max-h-72 overflow-y-auto">
                        {(data.hsn_summary || []).map((h, i) => (
                            <div key={`${h.hsn_sac}-${h.gst_rate_pct}` || i} className="px-3 py-2 border-b border-card text-xs flex justify-between">
                                <span className="font-mono text-primary">{h.hsn_sac}</span>
                                <span className="text-muted-foreground">{h.gst_rate_pct}%</span>
                                <span className="text-foreground">{inr(h.taxable_value)}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}

function Gstr3bView({ data }) {
    const s31 = data.section_3_1?.outward_taxable_supplies || {};
    const tax = data.section_6_1?.tax_payable || {};
    return (
        <div className="space-y-4">
            <div className="grid sm:grid-cols-4 gap-3">
                <Stat label="Invoices" value={data.doc_summary?.total_invoices ?? 0}/>
                <Stat label="IGST" value={inr(s31.integrated_tax)}/>
                <Stat label="CGST" value={inr(s31.central_tax)}/>
                <Stat label="SGST" value={inr(s31.state_tax)}/>
            </div>
            <div className="border border-border bg-card p-4">
                <div className="text-[10px] uppercase tracking-[0.16em] text-primary mb-3">§3.1 Outward taxable supplies (regular)</div>
                <table className="w-full text-xs">
                    <tbody>
                        <tr className="border-b border-card"><td className="py-1.5 text-muted-foreground">Taxable value</td><td className="py-1.5 text-right text-foreground">{inr(s31.taxable_value)}</td></tr>
                        <tr className="border-b border-card"><td className="py-1.5 text-muted-foreground">Integrated tax (IGST)</td><td className="py-1.5 text-right text-foreground">{inr(s31.integrated_tax)}</td></tr>
                        <tr className="border-b border-card"><td className="py-1.5 text-muted-foreground">Central tax (CGST)</td><td className="py-1.5 text-right text-foreground">{inr(s31.central_tax)}</td></tr>
                        <tr className="border-b border-card"><td className="py-1.5 text-muted-foreground">State/UT tax (SGST)</td><td className="py-1.5 text-right text-foreground">{inr(s31.state_tax)}</td></tr>
                    </tbody>
                </table>
            </div>
            <div className="border border-border bg-card p-4">
                <div className="text-[10px] uppercase tracking-[0.16em] text-primary mb-3">§6.1 Tax payable</div>
                <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-3">
                    <div><div className="text-[10px] text-muted-foreground">IGST</div><div className="text-lg text-primary">{inr(tax.integrated_tax)}</div></div>
                    <div><div className="text-[10px] text-muted-foreground">CGST</div><div className="text-lg text-primary">{inr(tax.central_tax)}</div></div>
                    <div><div className="text-[10px] text-muted-foreground">SGST</div><div className="text-lg text-primary">{inr(tax.state_tax)}</div></div>
                </div>
                <p className="text-[10px] text-muted-foreground mt-3">Note: §4 (Eligible ITC) requires a purchase journal — not yet implemented. §5 (Exempt/Nil) is zero by default.</p>
            </div>
        </div>
    );
}
